/**
 * Exportación de listados a Excel.
 *
 * Recibe las filas ya "humanizadas" (un objeto por fila, con las cabeceras
 * como claves y los valores ya legibles) y genera el fichero en el navegador.
 *
 * La librería `xlsx` se carga de forma dinámica para no engordar el bundle
 * inicial: solo se descarga la primera vez que alguien exporta. Si no
 * estuviera disponible, se genera un CSV con BOM (Excel lo abre igual).
 */

const COLUMN_WIDTH_CAP = 60;

const buildCsv = (rows) => {
  const header = Object.keys(rows[0]);
  return [header, ...rows.map((row) => header.map((h) => row[h] ?? ''))]
    .map((line) => line.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
};

const downloadCsv = (rows, fileName) => {
  const blob = new Blob(['\uFEFF' + buildCsv(rows)], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** Ancho de cada columna ajustado al contenido (con tope, para no acabar con
 *  una columna gigante por un valor muy largo). */
export const fitColumnWidths = (rows) =>
  Object.keys(rows[0]).map((h) => ({
    wch: Math.min(
      COLUMN_WIDTH_CAP,
      rows.reduce((max, row) => Math.max(max, String(row[h] ?? '').length + 2), h.length + 2)
    )
  }));

/**
 * Descarga las filas en un .xlsx (o .csv si la librería no está disponible).
 *
 * @param {Array<Object>} rows      filas con cabeceras legibles como claves
 * @param {Object}        options
 * @param {string}        options.fileBase  nombre base del fichero, sin fecha ni extensión
 * @param {string}        options.sheetName nombre de la hoja
 * @returns {Promise<boolean>} true si se generó la descarga
 */
export async function exportRowsToExcel(rows, { fileBase = 'listado', sheetName = 'Listado' } = {}) {
  if (!rows || !rows.length) return false;
  const stamp = new Date().toISOString().slice(0, 10);
  try {
    const XLSX = await import('xlsx');
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet['!cols'] = fitColumnWidths(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    XLSX.writeFile(workbook, `${fileBase}-${stamp}.xlsx`);
    return true;
  } catch {
    downloadCsv(rows, `${fileBase}-${stamp}.csv`);
    return true;
  }
}

/**
 * Igual que `exportRowsToExcel` pero acepta varios bloques:
 * `{ name, rows }` → una hoja por bloque en el mismo fichero.
 * Útil para inventarios con detalle (p.ej. servidores + sus discos).
 */
export async function exportSheetsToExcel(sheets, { fileBase = 'listado' } = {}) {
  const usable = (sheets || []).filter((s) => s && Array.isArray(s.rows) && s.rows.length);
  if (!usable.length) return false;
  const stamp = new Date().toISOString().slice(0, 10);
  try {
    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();
    usable.forEach(({ name, rows }) => {
      const sheet = XLSX.utils.json_to_sheet(rows);
      sheet['!cols'] = fitColumnWidths(rows);
      // Los nombres de hoja de Excel no admiten : \ / ? * [ ] y máx. 31 caracteres.
      XLSX.utils.book_append_sheet(
        workbook,
        sheet,
        String(name || 'Hoja').replace(/[:\\/?*[\]]/g, '-').slice(0, 31)
      );
    });
    XLSX.writeFile(workbook, `${fileBase}-${stamp}.xlsx`);
    return true;
  } catch {
    downloadCsv(usable[0].rows, `${fileBase}-${stamp}.csv`);
    return true;
  }
}
